import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiActivity, 
  FiFilter, 
  FiSearch, 
  FiChevronLeft, 
  FiChevronRight,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiClipboard,
  FiUser,
  FiUsers,
  FiFileText,
  FiHelpCircle,
  FiLoader,
  FiRefreshCw,
  FiCalendar,
  FiX,
  FiArrowLeft
} from 'react-icons/fi';
import { dashboardApi } from '../services/api';

const Activity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [entityType, setEntityType] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Column filters (advanced: two conditions per column)
  const defaultFilterState = () => ({ operator1: 'contains', value1: '', logic: 'and', operator2: 'equals', value2: '' });
  const [columnFilters, setColumnFilters] = useState({
    action: defaultFilterState(),
    entityType: defaultFilterState(),
    entityName: defaultFilterState(),
    details: defaultFilterState(),
    userEmail: defaultFilterState(),
    userRole: defaultFilterState(),
  });
  const [appliedFilters, setAppliedFilters] = useState({
    action: defaultFilterState(),
    entityType: defaultFilterState(),
    entityName: defaultFilterState(),
    details: defaultFilterState(),
    userEmail: defaultFilterState(),
    userRole: defaultFilterState(),
  });
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const filterRef = useRef(null);

  const entityTypes = [
    { value: '', label: 'All Types' },
    { value: 'User', label: 'Users' },
    { value: 'Organization', label: 'Organizations' },
    { value: 'Assessment', label: 'Assessments' },
    { value: 'Questionnaire', label: 'Questionnaires' },
    { value: 'Question', label: 'Questions' },
  ];

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardApi.getActivities(
        page, 
        pageSize, 
        entityType || null, 
        actionFilter || null, 
        userFilter || null, 
        fromDate || null, 
        toDate || null
      );
      setActivities(result.data || []);
      setTotalCount(result.totalCount || 0);
      setTotalPages(result.totalPages || 0);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, entityType, actionFilter, userFilter, fromDate, toDate]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleSearch = () => {
    setPage(1);
    fetchActivities();
  };

  const clearFilters = () => {
    setEntityType('');
    setActionFilter('');
    setUserFilter('');
    setFromDate('');
    setToDate('');
    setPage(1);
    // Also clear column filters
    const emptyFilters = {
      action: defaultFilterState(),
      entityType: defaultFilterState(),
      entityName: defaultFilterState(),
      details: defaultFilterState(),
      userEmail: defaultFilterState(),
      userRole: defaultFilterState(),
    };
    setColumnFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  // Column filter evaluation functions
  const evalCondition = (cellVal, operator, filterVal) => {
    if (!filterVal) return null;
    const cell = String(cellVal ?? '').toLowerCase();
    const fv = filterVal.toLowerCase();
    switch (operator) {
      case 'contains': return cell.includes(fv);
      case 'equals': return cell === fv;
      case 'starts-with': return cell.startsWith(fv);
      case 'ends-with': return cell.endsWith(fv);
      case 'not-equals': return cell !== fv;
      case 'not-contains': return !cell.includes(fv);
      default: return null;
    }
  };

  const evalColumnFilter = (cellVal, filter) => {
    const r1 = evalCondition(cellVal, filter.operator1, filter.value1);
    const r2 = evalCondition(cellVal, filter.operator2, filter.value2);
    if (r1 === null && r2 === null) return true;
    if (r1 === null) return r2;
    if (r2 === null) return r1;
    return filter.logic === 'and' ? r1 && r2 : r1 || r2;
  };

  // Apply column filters to activities
  const filteredActivities = activities.filter(activity => {
    return evalColumnFilter(activity.action, appliedFilters.action)
      && evalColumnFilter(activity.entityType, appliedFilters.entityType)
      && evalColumnFilter(activity.entityName, appliedFilters.entityName)
      && evalColumnFilter(activity.details, appliedFilters.details)
      && evalColumnFilter(activity.userEmail, appliedFilters.userEmail)
      && evalColumnFilter(activity.userRole, appliedFilters.userRole);
  });

  // Close filter popover on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setActiveFilter(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterClick = (e, columnKey) => {
    e.stopPropagation();
    if (activeFilter === columnKey) { setActiveFilter(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const popoverWidth = 260;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth) left = window.innerWidth - popoverWidth - 8;
    setFilterPosition({ top: rect.bottom + 4, left });
    setColumnFilters(f => ({ ...f, [columnKey]: { ...appliedFilters[columnKey] } }));
    setActiveFilter(columnKey);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(f => ({ ...f, [activeFilter]: { ...columnFilters[activeFilter] } }));
    setActiveFilter(null);
  };

  const handleClearColumnFilter = () => {
    const empty = defaultFilterState();
    setColumnFilters(f => ({ ...f, [activeFilter]: empty }));
    setAppliedFilters(f => ({ ...f, [activeFilter]: empty }));
    setActiveFilter(null);
  };

  const hasActiveFilter = (key) => appliedFilters[key].value1 !== '' || appliedFilters[key].value2 !== '';

  const getEntityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'user': return <FiUser className="text-blue-500" />;
      case 'organization': return <FiUsers className="text-purple-500" />;
      case 'assessment': return <FiClipboard className="text-emerald-500" />;
      case 'questionnaire': return <FiFileText className="text-orange-500" />;
      case 'question': return <FiHelpCircle className="text-amber-500" />;
      default: return <FiActivity className="text-slate-500" />;
    }
  };

  const getActionBadgeColor = (action) => {
    const actionLower = (action || '').toLowerCase();
    if (actionLower.includes('created')) return 'bg-emerald-100 text-emerald-700';
    if (actionLower.includes('updated')) return 'bg-blue-100 text-blue-700';
    if (actionLower.includes('deleted')) return 'bg-red-100 text-red-700';
    if (actionLower.includes('alert') || actionLower.includes('failed')) return 'bg-red-100 text-red-700';
    if (actionLower.includes('pending') || actionLower.includes('review')) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (action) => {
    const actionLower = (action || '').toLowerCase();
    if (actionLower.includes('created') || actionLower.includes('completed')) {
      return <FiCheckCircle className="text-emerald-500" />;
    }
    if (actionLower.includes('pending') || actionLower.includes('review')) {
      return <FiClock className="text-amber-500" />;
    }
    if (actionLower.includes('deleted') || actionLower.includes('alert') || actionLower.includes('failed')) {
      return <FiAlertCircle className="text-red-500" />;
    }
    return <FiActivity className="text-blue-500" />;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiLoader className="w-8 h-8 animate-spin text-[#0077b5]" />
        <span className="ml-2 text-slate-500">Loading activities...</span>
      </div>
    );
  }

  if (error && activities.length === 0) {
    return (
      <div className="rounded-xl border p-4 bg-red-50 border-red-200 text-red-600">
        Error loading activities: {error}
        <button onClick={fetchActivities} className="ml-4 underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0077b5] transition-colors"
      >
        <FiArrowLeft className="w-4 h-4" />
        Back
      </button>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiActivity className="text-[#0077b5]" />
            Activity Log
          </h1>
          <p className="mt-1 text-slate-500">
            Real-time tracking of all user activities across the platform
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={fetchActivities}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg text-sm font-medium flex items-center gap-2 ${
              showFilters ? 'bg-[#0077b5] text-white border-[#0077b5]' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <FiFilter /> Filters
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Filter Activities</h3>
            <button onClick={clearFilters} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
              <FiX size={14} /> Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
              <select 
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
              >
                {entityTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Action</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  placeholder="e.g., Created, Updated"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">User</label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  placeholder="Filter by user"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">From Date</label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">To Date</label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button 
              onClick={handleSearch}
              className="px-4 py-2 bg-[#0077b5] text-white rounded-lg text-sm font-medium hover:bg-[#006699]"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FiActivity className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{totalCount}</p>
              <p className="text-sm text-slate-500">Total Activities</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <FiCheckCircle className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => (a.action || '').toLowerCase().includes('created')).length}
              </p>
              <p className="text-sm text-slate-500">Created (page)</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <FiClock className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => (a.action || '').toLowerCase().includes('updated')).length}
              </p>
              <p className="text-sm text-slate-500">Updated (page)</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <FiAlertCircle className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => (a.action || '').toLowerCase().includes('deleted')).length}
              </p>
              <p className="text-sm text-slate-500">Deleted (page)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activities Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    Action 
                    <button onClick={(e) => handleFilterClick(e, 'action')} className={`p-0.5 rounded ${hasActiveFilter('action') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    Entity 
                    <button onClick={(e) => handleFilterClick(e, 'entityType')} className={`p-0.5 rounded ${hasActiveFilter('entityType') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    Details 
                    <button onClick={(e) => handleFilterClick(e, 'details')} className={`p-0.5 rounded ${hasActiveFilter('details') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    User Email 
                    <button onClick={(e) => handleFilterClick(e, 'userEmail')} className={`p-0.5 rounded ${hasActiveFilter('userEmail') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center justify-between">
                    Role 
                    <button onClick={(e) => handleFilterClick(e, 'userRole')} className={`p-0.5 rounded ${hasActiveFilter('userRole') ? 'text-[#0077b5]' : 'text-slate-400 hover:text-slate-600'}`}>
                      <FiFilter className="w-3 h-3" />
                    </button>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredActivities.map((activity, index) => (
                <tr key={activity.id || index} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      {getStatusIcon(activity.action)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getActionBadgeColor(activity.action)}`}>
                      {activity.action || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getEntityIcon(activity.entityType)}
                      <div>
                        <p className="text-sm font-medium text-slate-800">{activity.entityType || '-'}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[200px]">{activity.entityName || '-'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-slate-600 truncate max-w-[250px]">{activity.details || '-'}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#0077b5] flex items-center justify-center text-white text-xs">
                        {(activity.userEmail || 'U')[0].toUpperCase()}
                      </div>
                      <span className="text-sm text-slate-600">{activity.userEmail || '-'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      {activity.userRole || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm text-slate-600">{formatTimestamp(activity.timestamp)}</p>
                      <p className="text-xs text-slate-400">{activity.timeAgo || '-'}</p>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredActivities.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-500">
                    No activities found. Activities will appear here when users perform actions.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Column filter popover */}
        {activeFilter && (
          <div 
            ref={filterRef} 
            style={{ position: 'fixed', top: filterPosition.top, left: filterPosition.left, zIndex: 99999 }} 
            className="w-64 rounded-lg shadow-lg border bg-white border-slate-200 p-4"
          >
            <p className="text-sm font-semibold text-slate-800 mb-3">Filter</p>

            {/* First condition */}
            <select 
              value={columnFilters[activeFilter]?.operator1} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], operator1: e.target.value } }))} 
              className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-2"
            >
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="starts-with">Starts with</option>
              <option value="ends-with">Ends with</option>
              <option value="not-equals">Not equals</option>
              <option value="not-contains">Does not contain</option>
            </select>
            <input 
              type="text" 
              autoFocus 
              placeholder="Enter value..." 
              value={columnFilters[activeFilter]?.value1} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], value1: e.target.value } }))} 
              className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-3" 
            />

            {/* Logic operator */}
            <select 
              value={columnFilters[activeFilter]?.logic} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], logic: e.target.value } }))} 
              className="w-24 px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-3"
            >
              <option value="and">And</option>
              <option value="or">Or</option>
            </select>

            {/* Second condition */}
            <select 
              value={columnFilters[activeFilter]?.operator2} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], operator2: e.target.value } }))} 
              className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-2"
            >
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="starts-with">Starts with</option>
              <option value="ends-with">Ends with</option>
              <option value="not-equals">Not equals</option>
              <option value="not-contains">Does not contain</option>
            </select>
            <input 
              type="text" 
              placeholder="Enter value..." 
              value={columnFilters[activeFilter]?.value2} 
              onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], value2: e.target.value } }))} 
              className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-slate-300 text-slate-700 mb-4" 
            />

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button 
                onClick={handleClearColumnFilter} 
                className="px-4 py-1.5 text-sm font-semibold border border-[#F47F2E] text-[#F47F2E] rounded hover:bg-[#F47F2E]/10 transition-colors"
              >
                CLEAR
              </button>
              <button 
                onClick={handleApplyFilter} 
                className="px-4 py-1.5 text-sm font-semibold border border-[#F47F2E] text-[#F47F2E] rounded hover:bg-[#F47F2E]/10 transition-colors"
              >
                APPLY
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
            <div className="text-sm text-slate-500">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} activities
            </div>
            <div className="flex items-center gap-2">
              <select 
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
              >
                <FiChevronLeft />
              </button>
              <span className="px-3 py-1.5 text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity;
